import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { apiRequest } from "./queryClient";

// Declare LogRocket type for TypeScript
declare global {
  interface Window {
    LogRocket?: {
      identify: (userId: string, userInfo?: Record<string, any>) => void;
    };
  }
}

interface User {
  id: number;
  email: string;
  name: string;
  role: string;
  company?: string;
  jobTitle?: string;
  phone?: string;
  companyType?: string;
  managedBrands?: string[];
  profileImageUrl?: string;
  companyLogoUrl?: string;
  emailShowUpdates?: boolean;
  emailArtistUpdates?: boolean;
  emailMaxUpdates?: boolean;
  adminLevel?: string;
  createdAt?: Date;
}

interface AuthContextType {
  user: User | null;
  login: (email: string, password: string) => Promise<void>;
  register: (userData: RegisterData) => Promise<void>;
  logout: () => void;
  isLoading: boolean;
}

interface RegisterData {
  email: string;
  password: string;
  name: string;
  company?: string;
  role: string;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [, navigate] = useLocation();
  const queryClient = useQueryClient();

  // Check if user is logged in by checking for token
  const token = localStorage.getItem("token");
  const isLoggedIn = Boolean(token);

  // Use React Query to fetch user data when logged in
  const { data: userData, isLoading: isUserLoading } = useQuery({
    queryKey: ["/api/auth/me"],
    enabled: isLoggedIn,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: (failureCount, error: any) => {
      // If we get a 401 or 403, the token is invalid, don't retry
      if (error?.status === 401 || error?.status === 403) {
        localStorage.removeItem("token");
        setUser(null);
        return false;
      }
      return failureCount < 3;
    },
  });

  // Update user state when userData changes
  useEffect(() => {
    try {
      if (userData && typeof userData === 'object') {
        const user = userData as User;
        setUser(user);

        // Identify user in LogRocket for session replay
        if (window.LogRocket && user.id) {
          window.LogRocket.identify(user.id.toString(), {
            name: user.name,
            email: user.email,
            role: user.role,
            company: user.company,
            jobTitle: user.jobTitle,
            adminLevel: user.adminLevel,
          });
        }
      } else if (!isLoggedIn) {
        setUser(null);
      }
      setIsLoading(false);
    } catch (error) {
      // Handle auth error silently in production
      setUser(null);
      setIsLoading(false);
    }
  }, [userData, isLoggedIn]);

  const loginMutation = useMutation({
    mutationFn: async ({ email, password }: { email: string; password: string }) => {
      return await apiRequest("/api/auth/login", { method: "POST", body: { email, password } });
    },
    onSuccess: (data) => {
      localStorage.setItem("token", data.token);
      setUser(data.user);

      // Identify user in LogRocket for session replay
      if (window.LogRocket && data.user?.id) {
        window.LogRocket.identify(data.user.id.toString(), {
          name: data.user.name,
          email: data.user.email,
          role: data.user.role,
          company: data.user.company,
          jobTitle: data.user.jobTitle,
          adminLevel: data.user.adminLevel,
        });
      }

      queryClient.clear();
    },
    onError: (error) => {
      throw error;
    },
  });

  const registerMutation = useMutation({
    mutationFn: async (userData: RegisterData) => {
      return await apiRequest("/api/auth/register", { method: "POST", body: userData });
    },
    onSuccess: (data) => {
      localStorage.setItem("token", data.token);
      setUser(data.user);

      // Identify user in LogRocket for session replay
      if (window.LogRocket && data.user?.id) {
        window.LogRocket.identify(data.user.id.toString(), {
          name: data.user.name,
          email: data.user.email,
          role: data.user.role,
          company: data.user.company,
          jobTitle: data.user.jobTitle,
          adminLevel: data.user.adminLevel,
        });
      }

      queryClient.clear();
    },
    onError: (error) => {
      throw error;
    },
  });

  const login = async (email: string, password: string) => {
    return loginMutation.mutateAsync({ email, password });
  };

  const register = async (userData: RegisterData) => {
    return registerMutation.mutateAsync(userData);
  };

  const logout = () => {
    localStorage.removeItem("token");
    setUser(null);
    queryClient.clear();
    navigate("/login");
  };

  return (
    <AuthContext.Provider value={{
      user,
      login,
      register,
      logout,
      isLoading: isLoading || isUserLoading || loginMutation.isPending || registerMutation.isPending
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}

// Hook to get authenticated request headers
export function useAuthHeaders() {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
}
